import { Component } from "@angular/core";
import { MessageService } from "primeng/api";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import {
  InsiderRelationship,
  InsiderRelationshipMappingFilter,
  InsiderRelationshipToMap,
} from "src/api/models";
import { InsiderRelationshipMappingService } from "src/api/services";
import {
  handlePasteWithoutLeadingSpaces,
  preventLeadingSpaceKey,
} from "src/core/utils/input-validation.utils";
enum FormType {
  ALL = "All",
  PARENT = "Parent",
  CHILD = "Child",
  NONE = "None",
}
@Component({
  selector: "app-remap-insider-relationship",
  // imports: [],
  templateUrl: "./remap-insider-relationship.component.html",
  styleUrl: "./remap-insider-relationship.component.css",
  standalone: false,
})
export class RemapInsiderRelationshipComponent {
  selectedInsider: any = null;
  readonly paginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };
  readonly childPaginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };

  unprocessedInsiderRelationship: any[] = [];
  unprocessedChildInsiderRelationship: any[] = [];
  selectedParentRow: any = null;
  isChildSearchTriggered: boolean = false;
  selectedChildRows: any = null;
  selectedDialogRow: any = null;
  formTypes: FormType[] = Object.values(FormType);
  // Pagination for Security
  first = 0;
  rows = 10;
  childFirst = 0;
  childRows = 10;
  searchByParentName?: string;
  selectedColorType?: FormType | undefined;
  searchByChildName?: string;
  reason?: string;
  isLoading: boolean = false;
  isChildLoading: boolean = false;
  relationshipOptions: { label: string; value: string }[] = [];
  constructor(
    private _messageService: MessageService,
    private _insiderRelationshipMappingService: InsiderRelationshipMappingService,
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef
  ) {}
  ngOnInit() {
    const insider = this.normalizeInsider(this.config.data.insiders);
    this.unprocessedInsiderRelationship = [insider];
    this.unprocessedChildInsiderRelationship = [insider];
    this.selectedInsider = this.config.data.insiders;
    this.relationshipOptions = InsiderRelationship.values().map((v) => ({
      label: v,
      value: v,
    }));
  }

  /**
   * Ensure the insider object always exposes the comment on a consistent property
   * so the templates can bind to it reliably.
   */
  private normalizeInsider(insider: any): any {
    if (!insider) return insider;

    return {
      ...insider,
      // For parent table
      insiderComment:
        insider.insiderComment ?? insider.parentInsiderComment ?? insider.comment ?? null,
      // For child table – this is what the template binds to
      childInsiderComment:
        insider.childInsiderComment ?? insider.insiderComment ?? insider.comment ?? null,
    };
  }

  onParentCheckboxChange(insider: any) {
    if (
      this.selectedParentRow?.parentInsiderId === insider.parentInsiderId &&
      this.selectedParentRow?.insiderId === insider.insiderId
    ) {
      // Already selected, so deselect
      this.selectedParentRow = null;
    } else {
      // Select new one
      this.selectedParentRow = insider;
    }
  }

  loadData(event: any) {
    if (event.first === 0 && !event.forceLoad) {
      return;
    }
    if (event.first > 0 && this.paginator.pageSize != event.rows) {
      this.paginator.pageNumber = 1;
      this.first = 0;
    } else {
      this.paginator.pageNumber = event.first / event.rows + 1;
      this.first = event.first;
    }
    this.paginator.pageSize = event.rows;
    this.getUnprocessedInsiderRelationship();
  }
  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }
  onSearch() {
    this.first = 0;
    this.paginator.pageNumber = 1;
    this.getUnprocessedInsiderRelationship();
  }
  getUnprocessedInsiderRelationship() {
    const params: InsiderRelationshipMappingFilter = {
      pageNumber: this.paginator.pageNumber,
      pageSize: this.paginator.pageSize,
      relation: this.selectedColorType || "All",
      searchByName: this.searchByParentName,
    };
    this.isLoading = true;
    this._insiderRelationshipMappingService.getParentInsider(params).subscribe({
      next: (value) => {
        this.unprocessedInsiderRelationship = value ?? [];
      },
      error: (err) => {
        this.isLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.paginator.totalCount =
          this.unprocessedInsiderRelationship?.[0]?.totalCount ?? 0;
        this.isLoading = false;
      },
    });
  }
  getChildInsiderRelationship() {
    const params: InsiderRelationshipMappingFilter = {
      pageNumber: this.childPaginator.pageNumber,
      pageSize: this.childPaginator.pageSize,
      searchByName: this.searchByChildName || undefined,
      insiderIdToSkip:
        this.selectedParentRow?.insiderId ||
        this.selectedParentRow?.parentInsiderId ||
        null,
    };
    this.isChildLoading = true;
    this._insiderRelationshipMappingService.getChildInsider(params).subscribe({
      next: (value) => {
        // Normalize the collection so that existing comments from the API
        // are always available as `childInsiderComment` for the template.
        this.unprocessedChildInsiderRelationship = (value ?? []).map((row: any) =>
          this.normalizeInsider(row)
        );
      },
      error: (err) => {
        this.isChildLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.childPaginator.totalCount =
          this.unprocessedChildInsiderRelationship?.[0]?.totalCount ?? 0;
        this.isChildLoading = false;
      },
    });
  }
  loadChildData(event: any) {
    if (!this.isChildSearchTriggered) return;
    if (event.first > 0 && this.childPaginator.pageSize != event.rows) {
      this.childPaginator.pageNumber = 1;
      this.childFirst = 0;
    } else {
      this.childPaginator.pageNumber = event.first / event.rows + 1;
      this.childFirst = event.first;
    }
    this.childPaginator.pageSize = event.rows;
    this.getChildInsiderRelationship();
  }
  childPageChange(event: any) {
    this.childFirst = event.first;
    this.childRows = event.rows;
  }
  onChildSearch() {
    if (!this.selectedParentRow) {
      this._messageService.add({
        summary: "Please select a parent record first.",
        severity: "warn",
      });
      return;
    }
    this.childFirst = 0;
    this.childPaginator.pageNumber = 1;
    this.getChildInsiderRelationship();
  }
  onRemapClick() {
    if (
      this.selectedChildRows &&
      this.selectedChildRows.length > 0 &&
      (!this.reason || this.reason.trim() === "")
    ) {
      this._messageService.add({
        severity: "warn",
        summary: "Warning",
        detail: "Reason is required when child rows are selected.",
      });
      return;
    }

    // Deduplicate selected child insiders by their effective ID to avoid
    // sending the same insider multiple times in the payload.
    const uniqueChildrenMap = new Map<number, any>();
    (this.selectedChildRows || []).forEach((row: any) => {
      const id = row.insiderId || row.childInsiderId;
      if (!id) {
        return;
      }
      if (!uniqueChildrenMap.has(id)) {
        uniqueChildrenMap.set(id, row);
      }
    });
    const uniqueChildren = Array.from(uniqueChildrenMap.values());

    const params: InsiderRelationshipToMap = {
      childInsiderIds: uniqueChildren.map(
        (row: any) => row.insiderId || row.childInsiderId
      ),
      // Persist child insider comments; the UI binds to `childInsiderComment`,
      // but we also fall back to `insiderComment` to be safe.
      children: uniqueChildren.map((row: any) => ({
        insiderId: row.insiderId || row.childInsiderId,
        insiderComment:
          row.childInsiderComment ?? row.insiderComment ?? null,
      })),
      insiderComment:this.selectedParentRow?.insiderComment,
      insiderRelationShip: this.reason ?? (null as unknown as string),
      insiderRelationshipToDelete: {
        childInsiderId: this.selectedInsider.childInsiderId,
        childInsiderName: this.selectedInsider.childInsiderName,
        insiderRelationshipId: this.selectedInsider.insiderRelationshipId,
        parentInsiderId: this.selectedInsider?.parentInsiderId || null,
        parentInsiderName: this.selectedInsider?.parentInsiderName || "",
         insiderComment:this.selectedInsider?.insiderComment,
        reason: this.reason ?? (null as unknown as string),
        totalCount: 1,
      },
      parentInsiderId:
        this.selectedParentRow?.insiderId ||
        this.selectedParentRow?.parentInsiderId ||
        null,
    };
    this.isLoading = true;
    this._insiderRelationshipMappingService
      .mapInsiderRelationship(params)
      .subscribe({
        next: (value) => {
          this._messageService.add({
            summary: "Mapping successful",
            severity: "success",
          });

          //  Selection clear
          this.searchByChildName = undefined;
          this.searchByParentName = undefined;
          this.selectedColorType = undefined;
          this.selectedParentRow = null;
          this.selectedChildRows = [];
          this.reason = undefined;
        },

        error: (err) => {
          this.isLoading = false;
          let errorMessage = "An unexpected error occurred";

          try {
            // If backend returns a JSON string, parse it
            const parsedError = JSON.parse(err.error);
            errorMessage = parsedError.message || errorMessage;
          } catch {
            // If parsing fails, fallback to direct message or text
            errorMessage = err.error?.message || err.error || errorMessage;
          }
          this._messageService.add({
            summary: "Mapping failed",
            detail: errorMessage,
            severity: "error",
          });
        },
        complete: () => {
          this.isLoading = false;
          this.ref.close({ refresh: true });
        },
      });
  }
  preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }
  // One universal paste handler for ALL fields
  onPaste(
    event: ClipboardEvent,
    field: "searchByChildName" | "searchByParentName"
  ): void {
    handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
      this[field] = newValue;
    });
  }
}
